import {
  jotformContactFormId,
  jotformContactQids,
  jotformEmailFormId,
  jotformEmailQids,
} from '@/config/jotform.maps';

function jotformApiBase(): string {
  return (
    import.meta.env.VITE_JOTFORM_API_BASE?.replace(/\/$/, '') ||
    'https://api.jotform.com'
  );
}

function apiKey(): string | undefined {
  const k = import.meta.env.VITE_JOTFORM_API_KEY;
  return k && String(k).trim() ? String(k).trim() : undefined;
}

export function isJotformContactReady(): boolean {
  return Boolean(
    apiKey() &&
      jotformContactFormId &&
      Object.keys(jotformContactQids).length > 0
  );
}

export function isJotformEmailReady(): boolean {
  return Boolean(
    apiKey() &&
      jotformEmailFormId &&
      Object.keys(jotformEmailQids).length > 0
  );
}

function buildSubmissionBody(
  qids: Record<string, string>,
  data: Record<string, string>
): URLSearchParams {
  const body = new URLSearchParams();
  for (const [fieldName, qid] of Object.entries(qids)) {
    const v = data[fieldName];
    if (v === undefined || v === null) continue;
    const s = String(v).trim();
    if (s === '') continue;
    body.append(`submission[${qid}]`, s);
  }
  return body;
}

async function postSubmission(formId: string, body: URLSearchParams): Promise<void> {
  const key = apiKey();
  if (!key) throw new Error('Jotform: falta VITE_JOTFORM_API_KEY');

  const url = `${jotformApiBase()}/form/${formId}/submissions?apiKey=${encodeURIComponent(key)}`;
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body,
  });

  const text = await res.text();
  let json: { responseCode?: number; message?: string } = {};
  try {
    json = JSON.parse(text) as typeof json;
  } catch {
    /* no JSON */
  }

  if (!res.ok) {
    throw new Error(`Jotform HTTP ${res.status}: ${text.slice(0, 500)}`);
  }
  if (json.responseCode !== undefined && json.responseCode !== 200) {
    throw new Error(json.message || `Jotform responseCode ${json.responseCode}`);
  }
}

export async function submitJotformContact(
  fields: Record<string, string>
): Promise<void> {
  if (!isJotformContactReady()) return;
  const body = buildSubmissionBody(jotformContactQids, fields);
  await postSubmission(jotformContactFormId, body);
}

export async function submitJotformEmail(payload: {
  email: string;
  source: string;
}): Promise<void> {
  if (!isJotformEmailReady()) return;
  const body = buildSubmissionBody(jotformEmailQids, {
    email: payload.email.trim(),
    source: payload.source,
  });
  await postSubmission(jotformEmailFormId, body);
}
